import AssignmentTurnedIn from "@mui/icons-material/AssignmentTurnedIn";
import Delete from "@mui/icons-material/Delete";
import Download from "@mui/icons-material/Download";
import Edit from "@mui/icons-material/Edit";
import Refresh from "@mui/icons-material/Refresh";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { ExtractionStatus, TierIiReportEncampStatus } from "generated-graphql/graphql";
import { ReactElement } from "react";
import { canGeneratePermitRenewalForDocument } from "util/permitRenewal";

import { Row } from ".";

type GetDocumentsGridRowActionsParams = {
  row: Row;
  canWriteDocument: boolean;
  canGeneratePermitRenewals: boolean;
  hasDocumentAI: boolean;
  retryLoading: boolean;
  generatePermitRenewalsLoading: boolean;
  openEditDialog: (row: Row) => void;
  fetchAndHandleDownload: (args: { documentId: string }) => Promise<void>;
  handleRetryDocumentExtractions: (row: Row) => Promise<void>;
  handleGeneratePermitRenewal: (row: Row) => Promise<void>;
  onRemoveDocument: (row: Row) => void;
};

export function getDocumentsGridRowActions({
  row,
  canWriteDocument,
  canGeneratePermitRenewals,
  hasDocumentAI,
  retryLoading,
  generatePermitRenewalsLoading,
  openEditDialog,
  fetchAndHandleDownload,
  handleRetryDocumentExtractions,
  handleGeneratePermitRenewal,
  onRemoveDocument,
}: GetDocumentsGridRowActionsParams): ReactElement[] {
  const actions: ReactElement[] = [];
  const showGeneratePermitRenewal =
    canGeneratePermitRenewals && canGeneratePermitRenewalForDocument(row);

  if (canWriteDocument) {
    actions.push(
      <GridActionsCellItem
        key="edit-document"
        onClick={() => openEditDialog(row)}
        label="Edit Document"
        icon={<Edit />}
        disabled={row.extractionStatus === ExtractionStatus.Pending}
      />
    );
  }

  actions.push(
    <GridActionsCellItem
      key="download-document"
      onClick={async () =>
        await fetchAndHandleDownload({
          documentId: row.id,
        })
      }
      label="Download Document"
      icon={<Download />}
      disabled={row.extractionStatus === ExtractionStatus.Pending}
    />
  );

  if (canWriteDocument && hasDocumentAI) {
    actions.push(
      <GridActionsCellItem
        key="retry-extraction"
        showInMenu={true}
        onClick={async () => {
          await handleRetryDocumentExtractions(row);
        }}
        label="Retry Extraction"
        icon={<Refresh />}
        disabled={
          row.extractionStatus === ExtractionStatus.Pending || retryLoading || !row.tenantId
        }
      />
    );
  }

  if (showGeneratePermitRenewal) {
    actions.push(
      <GridActionsCellItem
        key="generate-permit-renewal"
        showInMenu={true}
        onClick={async () => {
          await handleGeneratePermitRenewal(row);
        }}
        label="Generate Permit Renewal"
        icon={<AssignmentTurnedIn />}
        disabled={generatePermitRenewalsLoading}
      />
    );
  }

  if (
    canWriteDocument &&
    (row.report == null || row.report.encampStatus !== TierIiReportEncampStatus.FilingComplete)
  ) {
    actions.push(
      <GridActionsCellItem
        key="remove-document"
        onClick={() => onRemoveDocument(row)}
        label="Remove Document"
        showInMenu={hasDocumentAI || showGeneratePermitRenewal}
        icon={<Delete />}
        disabled={row.extractionStatus === ExtractionStatus.Pending}
      />
    );
  }

  return actions;
}
